import React, { useEffect, useRef, useState } from 'react';
import { Search, ChevronDown, ChevronUp, Lock, X } from 'lucide-react';

const INSTRUMENT_OPTIONS = ['All', 'drums', 'melody', 'bass', 'texture', 'fx', 'vocals'];
// Studio Playground's 14 mood categories; the v1 loops API filters on these exact names.
const MOOD_OPTIONS = ['All', 'Calm', 'Happy', 'Sad', 'Energetic', 'Angry', 'Confident', 'Reflective', 'Hopeful', 'Chill', 'Dark', 'Focused', 'Warm', 'Playful', 'Tense'];
// The catalog's canonical key spelling ("Bb min"). The old list ("F Minor", "Cm") matched nothing.
const KEY_OPTIONS = ['All',
  'C maj', 'C# maj', 'D maj', 'Eb maj', 'E maj', 'F maj', 'F# maj', 'G maj', 'Ab maj', 'A maj', 'Bb maj', 'B maj',
  'C min', 'C# min', 'D min', 'Eb min', 'E min', 'F min', 'F# min', 'G min', 'Ab min', 'A min', 'Bb min', 'B min'];
const ENERGY_OPTIONS = ['All', '1', '2', '3', '4', '5'];

export const SearchFilters = ({
  searchQuery, onSearchChange,
  activeInstrument, onInstrumentChange,
  activeMood, onMoodChange,
  activeKey, onKeyChange,
  activeEnergy, onEnergyChange,
  activeBpm, onBpmChange,
  tempoLock, onReleaseTempoLock, onRelockTempo,
}) => {
  const [expanded, setExpanded] = useState(false);
  // BPM is typed, so wait for a pause before filtering instead of searching for
  // 1, then 12, then 120. Out-of-range values (a half-typed number) are not sent.
  const [bpmDraft, setBpmDraft] = useState(activeBpm ? String(activeBpm) : '');
  const bpmTimerRef = useRef(null);
  useEffect(() => { setBpmDraft(activeBpm ? String(activeBpm) : ''); }, [activeBpm]);
  useEffect(() => () => clearTimeout(bpmTimerRef.current), []);
  const handleBpmInput = (value) => {
    setBpmDraft(value);
    clearTimeout(bpmTimerRef.current);
    bpmTimerRef.current = setTimeout(() => {
      const bpm = parseInt(value, 10);
      if (!value) onBpmChange?.(null);
      else if (bpm >= 40 && bpm <= 220) onBpmChange?.(bpm);
    }, 400);
  };

  return (
    <div data-testid="search-filters" className="flex flex-col gap-2 px-3 py-2">
      {/* Search bar */}
      <div
        className="flex items-center gap-2 px-2.5 py-1.5"
        style={{
          background: 'var(--bg-elevated)', borderRadius: 22,
          border: '1px solid var(--border)',
        }}
      >
        <Search size={13} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
        <input
          data-testid="loop-search-input"
          className="flex-1 bg-transparent border-none outline-none"
          style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}
          placeholder="Search loops..."
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
      </div>

      {tempoLock?.active ? (
        <div className="flex items-center gap-1.5 px-2 py-1" style={{ background: 'var(--green)', borderRadius: 12, color: 'var(--bg)' }}>
          <Lock size={12} />
          <span className="flex-1" style={{ fontSize: 10, fontWeight: 700 }}>Matching your beat: {tempoLock.bpm} BPM</span>
          <button
            data-testid="release-tempo-lock"
            title="Show all tempos"
            aria-label="Show all tempos"
            onClick={() => onReleaseTempoLock?.()}
            className="flex items-center justify-center"
            style={{ color: 'var(--bg)', width: 18, height: 18 }}
          ><X size={13} /></button>
        </div>
      ) : tempoLock?.hasLibraryLoops ? (
        <button data-testid="relock-tempo" onClick={() => onRelockTempo?.()} className="self-start" style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)' }}>
          Match my beat&apos;s tempo
        </button>
      ) : null}

      {/* Instrument filter pills */}
      <div className="flex flex-wrap gap-1">
        {INSTRUMENT_OPTIONS.map((inst) => (
          <button
            key={inst}
            data-testid={`filter-instrument-${inst.toLowerCase()}`}
            onClick={() => onInstrumentChange?.(inst === 'All' ? null : inst)}
            className="transition-all"
            style={{
              fontSize: 10, fontWeight: 600,
              padding: '2px 10px', borderRadius: 22,
              background: activeInstrument === inst || (inst === 'All' && !activeInstrument)
                ? 'var(--green)' : 'var(--bg-elevated)',
              color: activeInstrument === inst || (inst === 'All' && !activeInstrument)
                ? 'var(--bg)' : 'var(--text-secondary)',
              border: '1px solid var(--border)',
            }}
          >
            {inst}
          </button>
        ))}
      </div>

      {/* Expand/collapse advanced filters */}
      <button
        data-testid="toggle-advanced-filters"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 self-start"
        style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-dim)' }}
      >
        {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        {expanded ? 'Less filters' : 'More filters'}
      </button>

      {/* Advanced filters (mood, key, energy, BPM) */}
      {expanded && (
        <div className="flex flex-col gap-2">
          {/* Mood */}
          <div>
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Mood</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {MOOD_OPTIONS.map((m) => (
                <button
                  key={m}
                  data-testid={`filter-mood-${m.toLowerCase()}`}
                  onClick={() => onMoodChange?.(m === 'All' ? null : m)}
                  style={{
                    fontSize: 9, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 22,
                    background: activeMood === m || (m === 'All' && !activeMood) ? 'var(--purple)' : 'var(--bg-elevated)',
                    color: activeMood === m || (m === 'All' && !activeMood) ? '#fff' : 'var(--text-dim)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Key */}
          <div>
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Key</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {KEY_OPTIONS.map((k) => (
                <button
                  key={k}
                  data-testid={`filter-key-${k.toLowerCase()}`}
                  onClick={() => onKeyChange?.(k === 'All' ? null : k)}
                  style={{
                    fontSize: 9, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 22,
                    background: activeKey === k || (k === 'All' && !activeKey) ? 'var(--blue)' : 'var(--bg-elevated)',
                    color: activeKey === k || (k === 'All' && !activeKey) ? '#fff' : 'var(--text-dim)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          {/* Energy */}
          <div>
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Energy</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {ENERGY_OPTIONS.map((e) => (
                <button
                  key={e}
                  data-testid={`filter-energy-${e.toLowerCase()}`}
                  onClick={() => onEnergyChange?.(e === 'All' ? null : parseInt(e))}
                  style={{
                    fontSize: 9, fontWeight: 600,
                    padding: '2px 8px', borderRadius: 22,
                    background: (activeEnergy !== null && activeEnergy !== undefined && String(activeEnergy) === e) || (e === 'All' && activeEnergy == null)
                      ? 'var(--orange)' : 'var(--bg-elevated)',
                    color: (activeEnergy !== null && activeEnergy !== undefined && String(activeEnergy) === e) || (e === 'All' && activeEnergy == null)
                      ? '#fff' : 'var(--text-dim)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {e === 'All' ? e : `${e} dots`}
                </button>
              ))}
            </div>
          </div>

          {/* BPM filter */}
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase' }}>BPM</span>
            <input
              data-testid="filter-bpm-input"
              type="number"
              placeholder="Any"
              value={bpmDraft}
              onChange={(e) => handleBpmInput(e.target.value)}
              className="outline-none text-center"
              style={{
                width: 52, height: 22, borderRadius: 22,
                background: 'var(--bg-elevated)', color: 'var(--text)',
                border: '1px solid var(--border)', fontSize: 10,
              }}
            />
            <span style={{ fontSize: 9, color: 'var(--text-dim)' }}>Also shows half and double time</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
